"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import posthog from "posthog-js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaCheckCircle, FaUndoAlt } from "react-icons/fa";
import Image from "next/image";
import { useParams, usePathname, useSearchParams } from "next/navigation";
import { AuthSurface } from "@/components/auth";
import { MfaDialog } from "@/components/mfa";
import { PatientProfileBadge } from "@/components/patient/patient-profile-badge";
import { Button } from "@/components/ui";
import { useIsMobile } from "@/contexts/is-mobile-provider";
import { useSelfScheduleContext } from "@/contexts/self-schedule-provider";
import { usePatientClaim } from "@/hooks/patient-claim/use-patient-claim";
import { useSignOutPatient } from "@/hooks/patient-claim/use-sign-out-patient";
import { usePatientVerification } from "@/hooks/self-schedule/use-patient-verification";
import { isEmbedPath } from "@/lib/embed-links";
import { formatPhoneNumber } from "@/lib/format-data";
import { PATIENT_PERMISSIONS, hasPatientPermission } from "@/lib/patient-permissions";
import { cx } from "@/lib/tailwind-utils";
import { toast } from "@/lib/use-toast";
import { EventStatus } from "@/types/enums";
import { BackButton } from "./components/back-button";
import { BookingEventPass } from "./components/booking-event-pass";
import { CarePlanSuggestionCard } from "./components/care-plan-suggestion-card";
import { ClinicCarousel } from "./components/clinic-carousel";
import { ExistingAppointmentCard } from "./components/existing-appointment-card";
import { RwgLandingBanner } from "./components/rwg-landing-banner";
import { ShopItineraryPrompt } from "./components/shop-cta";
import SelfScheduleSkeleton from "./self-schedule-skeleton";
import { LoadError } from "./status-screens";
import AppointmentReview from "./steps/appointment-review-step/appointment-review";
import { BookingComplete } from "./steps/booking-complete";
import Checkout from "./steps/checkout";
import Confirmation, { ConfirmationDrawer } from "./steps/confirmation";
import { LocationTypeStep } from "./steps/location-type";
import Locations from "./steps/locations-step/locations";
import { PatientTypeStep } from "./steps/patient-type";
import PaymentMethodStep from "./steps/payment-method-step";
import SecureAppointment from "./steps/secure-appointment";
import { useServicesView } from "./steps/services-step/components/use-services-view";
import Services from "./steps/services-step/services";
import { SelfScheduleServicesProvider } from "./steps/services-step/services-provider-self-schedule";
import TimePicker from "./steps/time-picker/time-picker";
import { WaitlistComplete } from "./steps/waitlist-complete";
import { selfScheduleQueryOptions } from "@/api/check-in/self-schedule/self-schedule-query";
import { tenantQueryOptions } from "@/query/tenant";
import { SelfScheduleStep, isSelfScheduleComplete } from "@/stores/self-schedule-store";
export default function SelfSchedulePage() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SelfScheduleServicesProvider><SelfSchedulePageInner /></SelfScheduleServicesProvider>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function SelfSchedulePageInner() {
  const $ = _c(143);
  const {
    currentStep,
    playNextStepAnimation,
    startOver,
    dismissedExistingAppointment,
    setDismissedExistingAppointment,
    dismissedCarePlanSuggestion,
    firstStep,
    cancelEventId,
    createdAppointmentId,
    previousStep,
    hasData,
    isRwgLanding,
    selectedLocation
  } = useSelfScheduleContext();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    effectiveNumAllowedServices,
    shouldUseCategorySidebar
  } = useServicesView(t0);
  const {
    isMobile
  } = useIsMobile();
  const [longerAnimationState, setLongerAnimationState] = useState(false);
  const [openMfaDialog, setOpenMfaDialog] = useState(false);
  const [openConfirmationDrawer, setOpenConfirmationDrawer] = useState(false);
  const [clickedRescheduleOrCancel, setClickedRescheduleOrCancel] = useState(false);
  const [processedAppointmentId, setProcessedAppointmentId] = useState(null);
  const capturedDeepLinkAppointmentIdRef = useRef(null);
  const {
    allFutureAppointments,
    blockingAppointment,
    patient,
    isResolvingSession
  } = usePatientVerification();
  const {
    firebaseUid,
    isPending: isPatientClaimPending
  } = usePatientClaim();
  const isPatientStateUnresolved = isResolvingSession || Boolean(firebaseUid) && isPatientClaimPending;
  const handleSignOut = useSignOutPatient(startOver);
  const {
    tenant
  } = useParams();
  const searchParams = useSearchParams();
  let t1;
  if ($[1] !== searchParams) {
    t1 = searchParams?.get("appointmentId") ?? null;
    $[1] = searchParams;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const appointmentIdParam = t1;
  const pathname = usePathname();
  let t2;
  if ($[3] !== pathname || $[4] !== searchParams) {
    t2 = isEmbedPath(pathname) && searchParams?.get("hideLogo") === "true";
    $[3] = pathname;
    $[4] = searchParams;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const hideLogo = t2;
  let t3;
  let t4;
  if ($[6] !== appointmentIdParam || $[7] !== patient || $[8] !== processedAppointmentId || $[9] !== setDismissedExistingAppointment) {
    t3 = () => {
      if (!appointmentIdParam || appointmentIdParam === processedAppointmentId) {
        return;
      }
      setProcessedAppointmentId(appointmentIdParam);
      setDismissedExistingAppointment(false);
      setClickedRescheduleOrCancel(true);
      if (!patient) {
        setOpenMfaDialog(true);
      }
    };
    t4 = [appointmentIdParam, patient, processedAppointmentId, setDismissedExistingAppointment];
    $[6] = appointmentIdParam;
    $[7] = patient;
    $[8] = processedAppointmentId;
    $[9] = setDismissedExistingAppointment;
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[12] !== allFutureAppointments || $[13] !== appointmentIdParam || $[14] !== createdAppointmentId || $[15] !== isPatientStateUnresolved || $[16] !== patient) {
    t5 = () => {
      if (!appointmentIdParam || isPatientStateUnresolved || capturedDeepLinkAppointmentIdRef.current === appointmentIdParam) {
        return;
      }
      capturedDeepLinkAppointmentIdRef.current = appointmentIdParam;
      posthog.capture("self_schedule_manage_appointment_clicked", {
        trigger: "deep_link",
        is_signed_in: Boolean(patient),
        existing_appointment_count: allFutureAppointments.filter(appointment => appointment.id !== createdAppointmentId).length
      });
    };
    t6 = [appointmentIdParam, isPatientStateUnresolved, patient, allFutureAppointments, createdAppointmentId];
    $[12] = allFutureAppointments;
    $[13] = appointmentIdParam;
    $[14] = createdAppointmentId;
    $[15] = isPatientStateUnresolved;
    $[16] = patient;
    $[17] = t5;
    $[18] = t6;
  } else {
    t5 = $[17];
    t6 = $[18];
  }
  useEffect(t5, t6);
  let t7;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      ...selfScheduleQueryOptions.frontPage(),
      select: _temp
    };
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const {
    data: frontPageData,
    isLoading: isLoadingFrontPage,
    isFetching: isFetchingFrontPage,
    refetch: refetchFrontPage
  } = useQuery(t7);
  let t8;
  if ($[20] !== tenant) {
    t8 = tenantQueryOptions.public(tenant);
    $[20] = tenant;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t8) {
    t9 = {
      ...t8
    };
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const {
    data: publicTenant,
    isLoading: isLoadingTenant,
    isFetching: isFetchingTenant,
    refetch: refetchTenant
  } = useQuery(t9);
  let t10;
  if ($[24] !== publicTenant) {
    t10 = hasPatientPermission(publicTenant, PATIENT_PERMISSIONS.scheduling.book);
    $[24] = publicTenant;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const canBookAppointments = t10;
  let t11;
  if ($[26] !== publicTenant) {
    t11 = hasPatientPermission(publicTenant, PATIENT_PERMISSIONS.scheduling.reschedule);
    $[26] = publicTenant;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const canRescheduleAppointments = t11;
  let t12;
  if ($[28] !== publicTenant) {
    t12 = hasPatientPermission(publicTenant, PATIENT_PERMISSIONS.scheduling.cancel);
    $[28] = publicTenant;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const canCancelAppointments = t12;
  const canManageExistingAppointments = canCancelAppointments || canRescheduleAppointments;
  let t13;
  if ($[30] !== allFutureAppointments) {
    t13 = allFutureAppointments.some(_temp2);
    $[30] = allFutureAppointments;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const hasReservedAppointment = t13;
  let t14;
  if ($[32] !== allFutureAppointments || $[33] !== createdAppointmentId) {
    let t15;
    if ($[35] !== createdAppointmentId) {
      t15 = appointment_1 => appointment_1.id !== createdAppointmentId;
      $[35] = createdAppointmentId;
      $[36] = t15;
    } else {
      t15 = $[36];
    }
    t14 = allFutureAppointments.filter(t15);
    $[32] = allFutureAppointments;
    $[33] = createdAppointmentId;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  const displayableExistingAppointments = t14;
  const displayableExistingAppointmentCount = displayableExistingAppointments.length;
  const hasDisplayableExistingAppointments = displayableExistingAppointmentCount > 0;
  const canDismissExistingAppointmentCards = !blockingAppointment && Boolean(publicTenant?.allowMultipleBookingsForDifferentAppointments);
  const showExistingAppointmentCard = (canManageExistingAppointments || hasReservedAppointment) && hasDisplayableExistingAppointments && !dismissedExistingAppointment && (!canDismissExistingAppointmentCards || clickedRescheduleOrCancel);
  const canUseSchedulingSteps = canBookAppointments || canRescheduleAppointments && Boolean(cancelEventId) || hasReservedAppointment && Boolean(createdAppointmentId);
  const canUseExistingAppointmentEntry = canManageExistingAppointments && (!patient || hasDisplayableExistingAppointments);
  const canUseCurrentSchedulingFlow = canUseSchedulingSteps || showExistingAppointmentCard || canUseExistingAppointmentEntry;
  const showCarePlanCard = canBookAppointments && !!patient?.nextCarePlanAppointment && !dismissedCarePlanSuggestion && !showExistingAppointmentCard && !cancelEventId;
  let t15;
  if ($[37] !== currentStep) {
    t15 = isSelfScheduleComplete(currentStep);
    $[37] = currentStep;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  const isComplete = t15;
  const showOverlayCards = !isComplete && (showCarePlanCard || showExistingAppointmentCard);
  const showFloatingServiceCounter = currentStep === SelfScheduleStep.SERVICE && (effectiveNumAllowedServices > 1 || isRwgLanding);
  let t16;
  let t17;
  if ($[39] !== clickedRescheduleOrCancel || $[40] !== displayableExistingAppointmentCount || $[41] !== patient) {
    t16 = () => {
      if (clickedRescheduleOrCancel && patient && displayableExistingAppointmentCount === 0) {
        toast({
          title: "No Appointment Found",
          description: "You do not have any appointments to reschedule or cancel."
        });
        posthog.capture("self_schedule_manage_appointment_not_found", {});
        setClickedRescheduleOrCancel(false);
      }
    };
    t17 = [clickedRescheduleOrCancel, displayableExistingAppointmentCount, patient];
    $[39] = clickedRescheduleOrCancel;
    $[40] = displayableExistingAppointmentCount;
    $[41] = patient;
    $[42] = t16;
    $[43] = t17;
  } else {
    t16 = $[42];
    t17 = $[43];
  }
  useEffect(t16, t17);
  let t18;
  let t19;
  if ($[44] !== dismissedExistingAppointment) {
    t18 = () => {
      if (dismissedExistingAppointment) {
        setClickedRescheduleOrCancel(false);
      }
    };
    t19 = [dismissedExistingAppointment];
    $[44] = dismissedExistingAppointment;
    $[45] = t18;
    $[46] = t19;
  } else {
    t18 = $[45];
    t19 = $[46];
  }
  useEffect(t18, t19);
  let t20;
  if ($[47] !== longerAnimationState || $[48] !== playNextStepAnimation) {
    t20 = () => {
      if (playNextStepAnimation === true && !longerAnimationState) {
        setLongerAnimationState(true);
        setTimeout(() => {
          setLongerAnimationState(false);
        }, 800);
      }
    };
    $[47] = longerAnimationState;
    $[48] = playNextStepAnimation;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== playNextStepAnimation) {
    t21 = [playNextStepAnimation];
    $[50] = playNextStepAnimation;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  useEffect(t20, t21);
  let t22;
  if ($[52] !== currentStep || $[53] !== firstStep) {
    t22 = currentStep === SelfScheduleStep.PAYMENT_METHOD || currentStep === SelfScheduleStep.SECURE_APPOINTMENT || currentStep === SelfScheduleStep.CHECKOUT || isSelfScheduleComplete(currentStep) || currentStep === firstStep;
    $[52] = currentStep;
    $[53] = firstStep;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  const shouldHideConfirmationDrawer = t22;
  let t23;
  let t24;
  if ($[55] !== currentStep || $[56] !== shouldHideConfirmationDrawer) {
    t23 = () => {
      if (currentStep === SelfScheduleStep.CONFIRMATION) {
        setOpenConfirmationDrawer(true);
      }
      if (shouldHideConfirmationDrawer) {
        setOpenConfirmationDrawer(false);
      }
    };
    t24 = [currentStep, shouldHideConfirmationDrawer];
    $[55] = currentStep;
    $[56] = shouldHideConfirmationDrawer;
    $[57] = t23;
    $[58] = t24;
  } else {
    t23 = $[57];
    t24 = $[58];
  }
  useEffect(t23, t24);
  let t25;
  if ($[59] !== currentStep || $[60] !== previousStep) {
    t25 = open => {
      setOpenConfirmationDrawer(open);
      if (!open && currentStep === SelfScheduleStep.CONFIRMATION) {
        previousStep();
      }
    };
    $[59] = currentStep;
    $[60] = previousStep;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  const handleOpenConfirmationDrawer = t25;
  let t26;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = [];
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  useEffect(_temp3, t26);
  if (isLoadingFrontPage || isLoadingTenant || isResolvingSession) {
    let t27;
    if ($[63] !== hideLogo) {
      t27 = <SelfScheduleSkeleton hideBanner={hideLogo} />;
      $[63] = hideLogo;
      $[64] = t27;
    } else {
      t27 = $[64];
    }
    return t27;
  }
  if (!frontPageData || !publicTenant) {
    if (isFetchingFrontPage || isFetchingTenant) {
      let t27;
      if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
        t27 = <SelfScheduleSkeleton />;
        $[65] = t27;
      } else {
        t27 = $[65];
      }
      return t27;
    }
    let t27;
    if ($[66] !== refetchFrontPage || $[67] !== refetchTenant) {
      t27 = <LoadError onRetry={() => {
        refetchFrontPage();
        refetchTenant();
      }} />;
      $[66] = refetchFrontPage;
      $[67] = refetchTenant;
      $[68] = t27;
    } else {
      t27 = $[68];
    }
    return t27;
  }
  const selectedLocationPhotoUrl = selectedLocation?.photoUrl;
  let t27;
  if ($[69] !== currentStep || $[70] !== firstStep || $[71] !== selectedLocationPhotoUrl) {
    t27 = currentStep !== null && currentStep !== firstStep && Boolean(selectedLocationPhotoUrl?.startsWith("http"));
    $[69] = currentStep;
    $[70] = firstStep;
    $[71] = selectedLocationPhotoUrl;
    $[72] = t27;
  } else {
    t27 = $[72];
  }
  const showsLocationPhoto = t27;
  const bannerImageUrl = showsLocationPhoto ? selectedLocationPhotoUrl : frontPageData?.selfScheduleBannerImageUrl;
  let t28;
  if ($[73] !== bannerImageUrl || $[74] !== hideLogo || $[75] !== showsLocationPhoto) {
    t28 = !hideLogo && <ClinicCarousel overrideImageUrl={bannerImageUrl} bannerAspect={!showsLocationPhoto} className="h-36 opacity-90 shadow-md md:mx-auto md:h-48 md:max-w-4xl" />;
    $[73] = bannerImageUrl;
    $[74] = hideLogo;
    $[75] = showsLocationPhoto;
    $[76] = t28;
  } else {
    t28 = $[76];
  }
  let t29;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <BackButton />;
    $[77] = t29;
  } else {
    t29 = $[77];
  }
  let t30;
  let t31;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <FaUndoAlt className="text-xxs" />;
    t31 = <span>Start over</span>;
    $[78] = t30;
    $[79] = t31;
  } else {
    t30 = $[78];
    t31 = $[79];
  }
  let t32;
  if ($[80] !== startOver) {
    t32 = <Button data-testid="start-over-button" variant="ghost" size="sm" onClick={startOver} className="self-center px-0" icon={t30}>{t31}</Button>;
    $[80] = startOver;
    $[81] = t32;
  } else {
    t32 = $[81];
  }
  let t33;
  if ($[82] !== handleSignOut || $[83] !== patient) {
    t33 = patient && <PatientProfileBadge firstName={patient.firstName} lastName={patient.lastName} onSignOut={handleSignOut} />;
    $[82] = handleSignOut;
    $[83] = patient;
    $[84] = t33;
  } else {
    t33 = $[84];
  }
  let t34;
  if ($[85] !== currentStep || $[86] !== frontPageData || $[87] !== hasData || $[88] !== hideLogo || $[89] !== isComplete) {
    t34 = frontPageData?.logoUrl && !hideLogo && <Image src={frontPageData.logoUrl} alt="Clinic logo" width={100} height={60} className={cx("z-10 h-9 object-contain opacity-90 w-auto transition-all duration-300 ease-in-out", hasData && currentStep !== SelfScheduleStep.CONFIRMATION && !isComplete && "md:mr-64")} />;
    $[85] = currentStep;
    $[86] = frontPageData;
    $[87] = hasData;
    $[88] = hideLogo;
    $[89] = isComplete;
    $[90] = t34;
  } else {
    t34 = $[90];
  }
  let t35;
  if ($[91] !== t32 || $[92] !== t33 || $[93] !== t34) {
    t35 = <div className="mx-auto flex max-w-4xl items-center justify-between px-4 md:px-0">{t29}<div className="flex items-end gap-3 mt-1 md:mb-4 md:mt-4">{t32}{t33}{t34}</div></div>;
    $[91] = t32;
    $[92] = t33;
    $[93] = t34;
    $[94] = t35;
  } else {
    t35 = $[94];
  }
  let t36;
  if ($[95] !== appointmentIdParam || $[96] !== canCancelAppointments || $[97] !== canRescheduleAppointments || $[98] !== canUseCurrentSchedulingFlow || $[99] !== canUseExistingAppointmentEntry || $[100] !== canUseSchedulingSteps || $[101] !== currentStep || $[102] !== displayableExistingAppointmentCount || $[103] !== firstStep || $[104] !== frontPageData || $[105] !== hasData || $[106] !== isComplete || $[107] !== isRwgLanding || $[108] !== patient || $[109] !== publicTenant?.name || $[110] !== selectedLocation || $[111] !== setDismissedExistingAppointment || $[112] !== shouldUseCategorySidebar || $[113] !== showCarePlanCard || $[114] !== showExistingAppointmentCard || $[115] !== showFloatingServiceCounter || $[116] !== showOverlayCards) {
    t36 = !canUseCurrentSchedulingFlow ? <div className="mx-auto mt-12 max-w-xl px-4 text-center"><div className="rounded-2xl border border-border bg-card p-8 shadow-sm"><h1 className="text-h2 text-foreground">Please contact the clinic in order to schedule an appointment</h1><p className="mt-3 text-body text-foreground-secondary">The clinic team will help you find the right appointment time.</p>{frontPageData?.phoneNumber && <a href={`tel:${frontPageData.phoneNumber}`} className="mt-6 inline-flex items-center justify-center rounded-xl bg-primary px-6 py-3 text-body font-semibold text-primary-foreground transition-opacity hover:opacity-90">Call {formatPhoneNumber(frontPageData.phoneNumber)}</a>}</div></div> : <div className="mx-auto flex w-full items-start justify-center gap-8 px-4 md:max-w-5xl">{(!hasData || isComplete) && <div className="hidden w-0 flex-shrink md:block" />}<div className={cx("w-full max-w-3xl transition-[max-width] duration-700 ease-in-out", currentStep === SelfScheduleStep.SERVICE && shouldUseCategorySidebar && "lg:max-w-5xl", currentStep === SelfScheduleStep.CONFIRMATION && "md:max-w-0 md:opacity-0")}>{showOverlayCards && <div className="mx-auto mt-12 flex max-w-md flex-col gap-4">{showCarePlanCard && <CarePlanSuggestionCard />}{showExistingAppointmentCard && <ExistingAppointmentCard targetAppointmentId={appointmentIdParam ?? undefined} canCancelAppointments={canCancelAppointments} canRescheduleAppointments={canRescheduleAppointments} />}</div>}{!showOverlayCards && <>{isRwgLanding && selectedLocation && (currentStep === SelfScheduleStep.SERVICE || currentStep === SelfScheduleStep.APPOINTMENT_REVIEW || currentStep === SelfScheduleStep.TIME) && <RwgLandingBanner location={selectedLocation} clinicName={publicTenant?.name} />}{canUseSchedulingSteps && <>{currentStep === SelfScheduleStep.PATIENT_TYPE && <PatientTypeStep />}{currentStep === SelfScheduleStep.LOCATION_TYPE && <LocationTypeStep />}{currentStep === SelfScheduleStep.SERVICE && <Services />}{currentStep === SelfScheduleStep.APPOINTMENT_REVIEW && <AppointmentReview />}{currentStep === SelfScheduleStep.LOCATION && <Locations />}{(currentStep === SelfScheduleStep.TIME || currentStep === SelfScheduleStep.CONFIRMATION) && <TimePicker />}{currentStep === SelfScheduleStep.PAYMENT_METHOD && <PaymentMethodStep />}{currentStep === SelfScheduleStep.SECURE_APPOINTMENT && <SecureAppointment />}{currentStep === SelfScheduleStep.CHECKOUT && <Checkout />}{currentStep === SelfScheduleStep.WAITLIST_COMPLETE && <WaitlistComplete />}{currentStep === SelfScheduleStep.BOOKING_COMPLETE && <BookingComplete />}</>}{currentStep === firstStep && canUseExistingAppointmentEntry && <Button variant="secondary" className={cx("flex items-center justify-center", "fixed left-1/2 -translate-x-1/2", showFloatingServiceCounter ? "bottom-[calc(10%_+_3.5rem)]" : "bottom-[10%]", "rounded-full", "px-4", "z-20", "motion-preset-slide-right-md", "shadow-md", "bg-background/70 text-foreground backdrop-blur-md")} onClick={() => {
            setDismissedExistingAppointment(false);
            setClickedRescheduleOrCancel(true);
            posthog.capture("self_schedule_manage_appointment_clicked", {
              trigger: "button",
              is_signed_in: Boolean(patient),
              existing_appointment_count: displayableExistingAppointmentCount
            });
            if (!patient) {
              setOpenMfaDialog(true);
            }
          }}><span>Reschedule or Cancel</span>{patient && displayableExistingAppointmentCount > 0 && <span className="ml-2 inline-flex min-w-5 items-center justify-center rounded-full bg-primary px-1.5 py-0.5 text-caption font-semibold text-primary-foreground">{displayableExistingAppointmentCount}</span>}</Button>}</>}{!isComplete && currentStep !== SelfScheduleStep.CONFIRMATION && <ShopItineraryPrompt className={cx("mb-32 mt-8 md:hidden", showOverlayCards && "mx-auto max-w-md")} />}</div>{!isComplete && <div className={cx("hidden flex-shrink transition-all duration-700 ease-in-out md:block", currentStep === SelfScheduleStep.CONFIRMATION ? shouldUseCategorySidebar ? "md:w-full md:max-w-5xl" : "md:w-full md:max-w-3xl" : "sticky top-[4.75rem]")}>{currentStep !== SelfScheduleStep.CONFIRMATION && <BookingEventPass className="mb-2" />}<div className={cx("max-h-96 transition-all duration-300 ease-in-out", currentStep === SelfScheduleStep.CONFIRMATION && "!max-h-0 overflow-hidden")}><ShopItineraryPrompt /></div><Confirmation className="w-full mt-2" useCategorySidebar={shouldUseCategorySidebar} /></div>}</div>;
    $[95] = appointmentIdParam;
    $[96] = canCancelAppointments;
    $[97] = canRescheduleAppointments;
    $[98] = canUseCurrentSchedulingFlow;
    $[99] = canUseExistingAppointmentEntry;
    $[100] = canUseSchedulingSteps;
    $[101] = currentStep;
    $[102] = displayableExistingAppointmentCount;
    $[103] = firstStep;
    $[104] = frontPageData;
    $[105] = hasData;
    $[106] = isComplete;
    $[107] = isRwgLanding;
    $[108] = patient;
    $[109] = publicTenant?.name;
    $[110] = selectedLocation;
    $[111] = setDismissedExistingAppointment;
    $[112] = shouldUseCategorySidebar;
    $[113] = showCarePlanCard;
    $[114] = showExistingAppointmentCard;
    $[115] = showFloatingServiceCounter;
    $[116] = showOverlayCards;
    $[117] = t36;
  } else {
    t36 = $[117];
  }
  let t37;
  if ($[118] !== longerAnimationState || $[119] !== playNextStepAnimation) {
    t37 = longerAnimationState && <div className="fixed left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2"><FaCheckCircle className={cx("aspect-square scale-150 text-6xl text-status-success-text motion-ease-spring-bouncier ", playNextStepAnimation ? "motion-preset-slide-up-lg motion-scale-in-75 motion-duration-300" : "motion-blur-out-lg motion-opacity-out-0 motion-duration-500")} /><div className={cx("absolute left-1/2 top-1/2 -z-10 aspect-square h-32 origin-top-left  -translate-x-1/2 -translate-y-1/2 rounded-full bg-status-success-text/30 opacity-50 blur-lg motion-ease-spring-bouncier", playNextStepAnimation ? "motion-preset-slide-up-lg motion-scale-in-75 motion-duration-300" : "blur-5xl motion-scale-out-0 motion-opacity-out-0 motion-duration-500")} /></div>;
    $[118] = longerAnimationState;
    $[119] = playNextStepAnimation;
    $[120] = t37;
  } else {
    t37 = $[120];
  }
  let t38;
  if ($[121] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = () => setOpenMfaDialog(false);
    $[121] = t38;
  } else {
    t38 = $[121];
  }
  let t39;
  if ($[122] !== openMfaDialog) {
    t39 = <MfaDialog open={openMfaDialog} onOpenChange={setOpenMfaDialog} surface={AuthSurface.SELF_SCHEDULE} onPatientVerified={t38} tAndSFooterText="booking your appointment" />;
    $[122] = openMfaDialog;
    $[123] = t39;
  } else {
    t39 = $[123];
  }
  let t40;
  if ($[124] !== currentStep || $[125] !== effectiveNumAllowedServices || $[126] !== firstStep || $[127] !== handleOpenConfirmationDrawer || $[128] !== hasData || $[129] !== isMobile || $[130] !== openConfirmationDrawer || $[131] !== shouldHideConfirmationDrawer) {
    t40 = !shouldHideConfirmationDrawer && <><Button variant="secondary" onClick={() => handleOpenConfirmationDrawer(true)} className={cx("flex items-center justify-center", "fixed left-1/2 -translate-x-1/2", "rounded-xl", "px-4", "motion-preset-slide-right-md", "shadow-md", "z-10", "md:hidden", "text-body-lg", "bg-background/80 backdrop-blur-sm", "transition-all duration-300 ease-in-out", hasData && !openConfirmationDrawer && currentStep !== firstStep ? currentStep === SelfScheduleStep.APPOINTMENT_REVIEW ? "bottom-4 text-body opacity-100" : "bottom-[5%] opacity-100" : "-bottom-4 opacity-0", currentStep === SelfScheduleStep.SERVICE && effectiveNumAllowedServices > 1 && "bottom-[4%] text-body")}><span>View Selections</span></Button>{isMobile && <ConfirmationDrawer isOpen={openConfirmationDrawer} setIsOpen={handleOpenConfirmationDrawer} />}</>;
    $[124] = currentStep;
    $[125] = effectiveNumAllowedServices;
    $[126] = firstStep;
    $[127] = handleOpenConfirmationDrawer;
    $[128] = hasData;
    $[129] = isMobile;
    $[130] = openConfirmationDrawer;
    $[131] = shouldHideConfirmationDrawer;
    $[132] = t40;
  } else {
    t40 = $[132];
  }
  let t41;
  if ($[133] !== frontPageData) {
    t41 = frontPageData?.phoneNumber && <footer className="mt-8 flex items-center justify-center pb-8 text-caption text-foreground-muted"><span>Need help? Call us at</span><a href={`tel:${frontPageData.phoneNumber}`} className="ml-1 text-link hover:text-link-hover"><span>{formatPhoneNumber(frontPageData.phoneNumber)}</span></a></footer>;
    $[133] = frontPageData;
    $[134] = t41;
  } else {
    t41 = $[134];
  }
  let t42;
  if ($[135] !== t28 || $[136] !== t35 || $[137] !== t36 || $[138] !== t37 || $[139] !== t39 || $[140] !== t40 || $[141] !== t41) {
    t42 = <div className="relative h-full">{t28}{t35}{t36}{t37}{t39}{t40}{t41}</div>;
    $[135] = t28;
    $[136] = t35;
    $[137] = t36;
    $[138] = t37;
    $[139] = t39;
    $[140] = t40;
    $[141] = t41;
    $[142] = t42;
  } else {
    t42 = $[142];
  }
  return t42;
}
function _temp3() {
  posthog.startSessionRecording({
    sampling: true
  });
}
function _temp2(appointment_0) {
  return appointment_0.status === EventStatus.RESERVED;
}
function _temp(data) {
  return data && {
    logoUrl: data.logoUrl,
    phoneNumber: data.phoneNumber,
    hasVisibleGiftCards: data.hasVisibleGiftCards ?? false,
    hasVisiblePackages: data.hasVisiblePackages ?? false,
    selfScheduleBannerImageUrl: data.selfScheduleBannerImageUrl
  };
}